import { useEffect } from 'react';
import type { QuizPlayersScoreData } from '@mentimeter/core-hooks';
import {
  QuizConfigProvider,
  useQuizMetaContext,
  useQuizResults,
  useSocketIoConnection,
} from '@mentimeter/quiz';
import { ClientErrorCodes, GameStateEnums } from '@mentimeter/quiz-types';
import { usePresentationContext } from '../../legacy-context/presentationContext';
import { Error } from '../../shared/components/quiz/error';
import { Loading } from '../../shared/components/quiz/loading';
import { CenteringContainer } from '../../shared/components/quiz/CenteringContainer';
import { TitleSection } from '../../shared/components/title-section/TitleSection';
import { FollowerPresentation } from './FollowerPresentation';
import { LivePresentation } from './LivePresentation';
import { useLeaderboardResults } from './useLeaderboardResults';

export const LivePresentationDataHandler = ({
  timeBasedScoring,
}: {
  timeBasedScoring: boolean;
}) => {
  const {
    data: results,
    mutate,
    error,
    isPreviewResults = false,
  } = useQuizResults<QuizPlayersScoreData>();
  const { useQuestion, useCurrentSession, useConfig, useVotingInstructions } =
    usePresentationContext();
  const { voteKey } = useVotingInstructions();
  const {
    id,
    question,
    title_meta,
    question_description,
    question_styled,
    public_key,
    slideAdminKey: admin_key,
    layout,
  } = useQuestion();
  const { currentSessionId: session } = useCurrentSession();
  const { isLastLeaderboard, follower } = useQuizMetaContext();
  const { connection } = useSocketIoConnection();
  const leaderboardResults = useLeaderboardResults(results);
  const { enableAnimations } = useConfig();

  // Invalidate results when session is updated
  useEffect(() => {
    return () => void mutate(undefined);
  }, [session, mutate]);

  if (error) {
    return <Error errorCode={ClientErrorCodes.RESULTS_FETCH} />;
  }

  if (!results || !connection || session === undefined) {
    return <Loading />;
  }

  if (follower) {
    return (
      <QuizConfigProvider
        questionPublicKey={public_key}
        questionAdminKey={admin_key}
        voteKey={voteKey}
      >
        <TitleSection
          title={question}
          styledTitle={question_styled}
          label={title_meta}
          description={question_description}
          fontSizeOffset={layout?.font_size_offset}
        />
        <CenteringContainer>
          <FollowerPresentation
            timeBasedScoring={timeBasedScoring}
            leaderboardResults={leaderboardResults}
            shouldAnimate={enableAnimations}
            mutateResults={mutate}
            questionId={id}
            initialGameState={
              isLastLeaderboard
                ? GameStateEnums.FINAL_LEADERBOARD
                : GameStateEnums.LEADERBOARD
            }
            isPreviewResults={isPreviewResults}
          />
        </CenteringContainer>
      </QuizConfigProvider>
    );
  }

  return (
    <QuizConfigProvider
      questionPublicKey={public_key}
      questionAdminKey={admin_key}
      voteKey={voteKey}
    >
      <TitleSection
        title={question}
        styledTitle={question_styled}
        label={title_meta}
        description={question_description}
        fontSizeOffset={layout?.font_size_offset}
      />
      <CenteringContainer>
        <LivePresentation
          activeSession={session}
          timeBasedScoring={timeBasedScoring}
          initialGameState={
            isLastLeaderboard
              ? GameStateEnums.FINAL_LEADERBOARD
              : GameStateEnums.LEADERBOARD
          }
          leaderboardResults={leaderboardResults}
          shouldAnimate={enableAnimations}
          questionId={id}
          isPreviewResults={isPreviewResults}
        />
      </CenteringContainer>
    </QuizConfigProvider>
  );
};
